import { useTranslation } from 'react-i18next'
import { DropdownMenu } from '@/components/ui/DropdownMenu'
import type { Group, Table } from '@/types'
import { getTableDisplayName } from '@/utils/normalizers'
import { SidebarTableItem } from './SidebarTableItem'

interface SidebarGroupProps {
  groupId: string
  group: Group
  tables: Table[]
  allTables: Table[]
  collapsed: boolean
  expandedTableIds: Record<string, boolean>
  selectedTableIds: Set<string>
  hiddenTableIdSet: Set<string>
  hasHiddenRelatedTables: boolean
  onToggleGroup: () => void
  onHideGroupTables: () => void
  onShowGroupTables: () => void
  onShowRelatedGroupTables: () => void
  onToggleTable: (tableId: string) => void
  onSelectTable: (table: Table) => void
  onColumnClick: (table: Table) => void
  onToggleVisibility: (table: Table) => void
  onEditTable: (table: Table) => void
  onDeleteTable: (table: Table) => void
  readOnly?: boolean
}

export function SidebarGroup({
  group,
  tables,
  allTables,
  collapsed,
  expandedTableIds,
  selectedTableIds,
  hiddenTableIdSet,
  hasHiddenRelatedTables,
  onToggleGroup,
  onHideGroupTables,
  onShowGroupTables,
  onShowRelatedGroupTables,
  onToggleTable,
  onSelectTable,
  onColumnClick,
  onToggleVisibility,
  onEditTable,
  onDeleteTable,
  readOnly = false,
}: SidebarGroupProps) {
  const { t } = useTranslation()
  const hiddenCount = tables.filter((table) => hiddenTableIdSet.has(table.id)).length
  const allHidden = hiddenCount === tables.length && tables.length > 0
  const anyHidden = hiddenCount > 0

  return (
    <section className="group">
      <div
        className="flex items-center gap-2 border-b px-3 py-2 text-[10px] uppercase tracking-[0.5px]"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-muted)',
        }}
      >
        <button
          type="button"
          onClick={onToggleGroup}
          className="flex min-w-0 flex-1 items-center gap-2 rounded text-left"
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'var(--sb-hover)'
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent'
          }}
        >
          <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: group.color }} />
          <span className="min-w-0 flex-1 truncate">{group.name}</span>
          <span className="text-[9px]" style={{ color: 'var(--text-dim)' }}>
            {hiddenCount > 0 ? `${hiddenCount}/${tables.length}` : tables.length}
          </span>
          <span
            className="text-[8px] transition-transform"
            style={{
              color: 'var(--text-dim)',
              transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            }}
          >
            ▶
          </span>
        </button>
        <DropdownMenu
          label={t('sidebar.groupActions')}
          icon={
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
              <circle cx="6" cy="2" r="1.2" />
              <circle cx="6" cy="6" r="1.2" />
              <circle cx="6" cy="10" r="1.2" />
            </svg>
          }
          iconOnly
          size="sm"
          borderless
          active={anyHidden}
          sections={[{
            id: 'visibility',
            items: [
              allHidden
                ? {
                    id: 'show-all',
                    icon: '◨',
                    title: t('sidebar.showGroup'),
                    onSelect: onShowGroupTables,
                  }
                : {
                    id: 'hide-all',
                    icon: '◫',
                    title: t('sidebar.hideGroup'),
                    onSelect: onHideGroupTables,
                  },
              {
                id: 'show-related',
                icon: '◈',
                title: t('contextMenu.showRelatedTables'),
                disabled: !hasHiddenRelatedTables,
                onSelect: onShowRelatedGroupTables,
              },
            ],
          }]}
        />
      </div>

      {!collapsed ? (
        <div className="py-1">
          {tables.map((table) => (
            <SidebarTableItem
              key={table.id}
              table={table}
              displayName={getTableDisplayName(table, allTables)}
              selected={selectedTableIds.has(table.id)}
              hidden={hiddenTableIdSet.has(table.id)}
              expanded={!!expandedTableIds[table.id]}
              accentColor={group.color}
              onToggleExpand={() => onToggleTable(table.id)}
              onSelect={() => onSelectTable(table)}
              onColumnClick={() => onColumnClick(table)}
              onToggleVisibility={() => onToggleVisibility(table)}
              onEdit={() => onEditTable(table)}
              onDelete={() => onDeleteTable(table)}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
